import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, User, Search, FileText, Mail, Trash2, Download, Upload } from 'lucide-react';
import api from '../lib/axios';
import { euro, dateFmt } from '../lib/format';

interface Contact {
  id: number;
  type: 'PERSON' | 'ORGANIZATION';
  lastName: string | null;
  firstName: string | null;
  displayName: string;
  legalName: string | null;
  tradeName: string | null;
  legalForm: string | null;
  siret: string | null;
  contactPerson: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  notes: string | null;
  active: boolean;
}

interface HistoryEntry {
  entryId: number;
  number: string;
  entryDate: string;
  label: string;
  campaignId: number | null;
  campaignLabel: string | null;
  amount: number;
}

interface ContactHistory {
  contactId: number;
  displayName: string;
  totalAmount: number;
  entryCount: number;
  entries: HistoryEntry[];
}

type ContactForm = {
  type: 'PERSON' | 'ORGANIZATION';
  lastName: string;
  firstName: string;
  legalName: string;
  tradeName: string;
  legalForm: string;
  siret: string;
  contactPerson: string;
  email: string;
  phone: string;
  mobile: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  country: string;
  notes: string;
};

const EMPTY: ContactForm = {
  type: 'PERSON',
  lastName: '',
  firstName: '',
  legalName: '',
  tradeName: '',
  legalForm: '',
  siret: '',
  contactPerson: '',
  email: '',
  phone: '',
  mobile: '',
  addressLine1: '',
  addressLine2: '',
  postalCode: '',
  country: 'France',
  city: '',
  notes: '',
};

async function downloadReceiptPdf(id: number, number: string) {
  const res = await api.get(`/receipts/${id}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${number}.pdf`;
  a.click();
  window.URL.revokeObjectURL(url);
}

export default function Contacts() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ContactForm>({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importInfo, setImportInfo] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['contacts', search],
    queryFn: () =>
      api.get<Contact[]>('/contacts', { params: search ? { q: search } : {} }).then((r) => r.data),
  });

  const history = useQuery({
    queryKey: ['contact-history', selectedId],
    queryFn: () =>
      api.get<ContactHistory>(`/contacts/${selectedId}/history`).then((r) => r.data),
    enabled: selectedId != null,
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        type: form.type,
        lastName: form.lastName.trim() || null,
        firstName: form.firstName.trim() || null,
        legalName: form.legalName.trim() || null,
        tradeName: form.tradeName.trim() || null,
        legalForm: form.legalForm.trim() || null,
        siret: form.siret.trim() || null,
        contactPerson: form.contactPerson.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        mobile: form.mobile.trim() || null,
        addressLine1: form.addressLine1.trim() || null,
        addressLine2: form.addressLine2.trim() || null,
        postalCode: form.postalCode.trim() || null,
        city: form.city.trim() || null,
        country: form.country.trim() || null,
        notes: form.notes.trim() || null,
      };
      return editingId != null
        ? api.put(`/contacts/${editingId}`, body).then((r) => r.data)
        : api.post('/contacts', body).then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      resetForm();
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? t('contacts.saveError')),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/contacts/${id}`).then((r) => r.data),
    onSuccess: (_d, id) => {
      if (selectedId === id) setSelectedId(null);
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? t('contacts.deleteError')),
  });

  const exportExcel = useMutation({
    mutationFn: async () => {
      const res = await api.get('/contacts/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contacts.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? t('contacts.exportError')),
  });

  const importExcel = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post<{ created: number; updated: number; skipped: number }>(
        '/contacts/import',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return res.data;
    },
    onSuccess: (r) => {
      setError(null);
      setImportInfo(
        t('contacts.importDone', { created: r.created, updated: r.updated, skipped: r.skipped }),
      );
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? t('contacts.importError')),
  });

  const onImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) importExcel.mutate(file);
    e.target.value = '';
  };

  const generateReceipt = useMutation({
    mutationFn: (row: HistoryEntry) =>
      api
        .post('/receipts', {
          contactId: selectedId,
          entryId: row.entryId,
          campaignId: row.campaignId,
          amount: row.amount,
        })
        .then((r) => r.data),
    onSuccess: (receipt: any) => {
      qc.invalidateQueries({ queryKey: ['contact-history', selectedId] });
      downloadReceiptPdf(receipt.id, receipt.receiptNumber);
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? t('receipts.generateError')),
  });

  const sendReceipt = useMutation({
    mutationFn: async (row: HistoryEntry) => {
      const receipt = await api
        .post('/receipts', {
          contactId: selectedId,
          entryId: row.entryId,
          campaignId: row.campaignId,
          amount: row.amount,
        })
        .then((r) => r.data);
      return api.post(`/receipts/${receipt.id}/send`).then((r) => r.data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contact-history', selectedId] }),
    onError: (e: any) => setError(e?.response?.data?.message ?? t('receipts.sendError')),
  });

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...EMPTY });
    setError(null);
  };

  const edit = (c: Contact) => {
    setEditingId(c.id);
    setForm({
      type: c.type,
      lastName: c.lastName ?? '',
      firstName: c.firstName ?? '',
      legalName: c.legalName ?? '',
      tradeName: c.tradeName ?? '',
      legalForm: c.legalForm ?? '',
      siret: c.siret ?? '',
      contactPerson: c.contactPerson ?? '',
      email: c.email ?? '',
      phone: c.phone ?? '',
      mobile: c.mobile ?? '',
      addressLine1: c.addressLine1 ?? '',
      addressLine2: c.addressLine2 ?? '',
      postalCode: c.postalCode ?? '',
      city: c.city ?? '',
      country: c.country ?? 'France',
      notes: c.notes ?? '',
    });
    setShowForm(true);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = () => {
    setError(null);
    if (form.type === 'ORGANIZATION') {
      if (!form.legalName.trim() && !form.tradeName.trim() && !form.email.trim()) {
        setError(t('contacts.missingOrgName'));
        return;
      }
    } else if (!form.lastName.trim() && !form.firstName.trim() && !form.email.trim()) {
      setError(t('contacts.missingName'));
      return;
    }
    save.mutate();
  };

  const contacts = data ?? [];

  return (
    <div>
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{t('nav.contacts')}</p>
          <h1 className="page-title mt-1">{t('contacts.title')}</h1>
          <p className="mt-1 text-sm text-ink-muted">{t('contacts.subtitle')}</p>
        </div>
        <button
          onClick={() => (showForm ? resetForm() : setShowForm(true))}
          className={showForm ? 'btn btn-secondary' : 'btn btn-primary'}
        >
          {showForm ? (
            t('common.close')
          ) : (
            <>
              <Plus size={16} /> {t('contacts.new')}
            </>
          )}
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button
          onClick={() => exportExcel.mutate()}
          disabled={exportExcel.isPending}
          className="btn btn-secondary"
        >
          <Download size={16} /> {t('contacts.export')}
        </button>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={importExcel.isPending}
          className="btn btn-secondary"
        >
          <Upload size={16} /> {importExcel.isPending ? t('contacts.importing') : t('contacts.import')}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          onChange={onImportFile}
          className="hidden"
        />
        {importInfo && <span className="text-sm font-medium text-income">{importInfo}</span>}
      </div>

      {showForm && (
        <div className="reveal card mb-6 p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="field-label">{t('contacts.type')}</label>
              <select
                value={form.type}
                onChange={(e) =>
                  setForm((f) => ({ ...f, type: e.target.value as ContactForm['type'] }))
                }
                className="select"
              >
                <option value="PERSON">{t('contacts.person')}</option>
                <option value="ORGANIZATION">{t('contacts.organization')}</option>
              </select>
            </div>
            <div />
            {form.type === 'ORGANIZATION' ? (
              <>
                <div>
                  <label className="field-label">{t('contacts.legalName')}</label>
                  <input
                    value={form.legalName}
                    onChange={(e) => setForm((f) => ({ ...f, legalName: e.target.value }))}
                    className="input"
                  />
                </div>
                <div>
                  <label className="field-label">{t('contacts.tradeName')}</label>
                  <input
                    value={form.tradeName}
                    onChange={(e) => setForm((f) => ({ ...f, tradeName: e.target.value }))}
                    className="input"
                  />
                </div>
                <div>
                  <label className="field-label">{t('contacts.legalForm')}</label>
                  <input
                    value={form.legalForm}
                    onChange={(e) => setForm((f) => ({ ...f, legalForm: e.target.value }))}
                    className="input"
                    placeholder={t('contacts.legalFormPlaceholder')}
                    list="legal-forms"
                  />
                  <datalist id="legal-forms">
                    <option value="SARL" />
                    <option value="SAS" />
                    <option value="SASU" />
                    <option value="EURL" />
                    <option value="SA" />
                    <option value="SCI" />
                    <option value="Auto-entrepreneur" />
                    <option value="Association" />
                  </datalist>
                </div>
                <div>
                  <label className="field-label">{t('contacts.siret')}</label>
                  <input
                    value={form.siret}
                    onChange={(e) => setForm((f) => ({ ...f, siret: e.target.value }))}
                    className="input"
                    placeholder="14 chiffres"
                    inputMode="numeric"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="field-label">{t('contacts.contactPerson')}</label>
                  <input
                    value={form.contactPerson}
                    onChange={(e) => setForm((f) => ({ ...f, contactPerson: e.target.value }))}
                    className="input"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="field-label">{t('contacts.firstName')}</label>
                  <input
                    value={form.firstName}
                    onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                    className="input"
                  />
                </div>
                <div>
                  <label className="field-label">{t('contacts.lastName')}</label>
                  <input
                    value={form.lastName}
                    onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                    className="input"
                  />
                </div>
              </>
            )}
            <div>
              <label className="field-label">{t('contacts.email')}</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="input"
              />
            </div>
            <div>
              <label className="field-label">{t('contacts.phone')}</label>
              <input
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                className="input"
              />
            </div>
            <div>
              <label className="field-label">{t('contacts.mobile')}</label>
              <input
                value={form.mobile}
                onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))}
                className="input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="field-label">{t('contacts.addressLine1')}</label>
              <input
                value={form.addressLine1}
                onChange={(e) => setForm((f) => ({ ...f, addressLine1: e.target.value }))}
                className="input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="field-label">{t('contacts.addressLine2')}</label>
              <input
                value={form.addressLine2}
                onChange={(e) => setForm((f) => ({ ...f, addressLine2: e.target.value }))}
                className="input"
              />
            </div>
            <div>
              <label className="field-label">{t('contacts.postalCode')}</label>
              <input
                value={form.postalCode}
                onChange={(e) => setForm((f) => ({ ...f, postalCode: e.target.value }))}
                className="input"
              />
            </div>
            <div>
              <label className="field-label">{t('contacts.city')}</label>
              <input
                value={form.city}
                onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                className="input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="field-label">{t('contacts.notes')}</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="input"
                rows={2}
              />
            </div>
          </div>

          <div className="mt-5 flex items-center gap-3">
            <div className="flex-1" />
            {error && <span className="text-sm font-medium text-expense">{error}</span>}
            <button onClick={submit} disabled={save.isPending} className="btn btn-primary">
              {save.isPending
                ? t('contacts.creating')
                : editingId != null
                ? t('contacts.update')
                : t('contacts.create')}
            </button>
          </div>
        </div>
      )}

      <div className="mb-4 flex items-center gap-2">
        <Search size={16} className="text-ink-muted" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input max-w-sm"
          placeholder={t('contacts.search')}
        />
      </div>

      {isLoading && <p className="text-ink-muted">{t('common.loading')}</p>}
      {!isLoading && contacts.length === 0 && (
        <div className="card p-8 text-center text-ink-muted">{t('contacts.empty')}</div>
      )}

      {contacts.length > 0 && (
        <div className="card overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t('contacts.lastName')}</th>
                <th>{t('contacts.email')}</th>
                <th>{t('contacts.phone')}</th>
                <th>{t('contacts.city')}</th>
                <th className="text-right">{t('field.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id}>
                  <td data-label={t('contacts.lastName')}>
                    <button
                      onClick={() => setSelectedId(c.id)}
                      className="flex items-center gap-2 font-medium text-brand hover:text-brand-strong"
                    >
                      <User size={16} className="text-ink-muted" />
                      {c.displayName}
                    </button>
                  </td>
                  <td data-label={t('contacts.email')} className="text-ink-soft">{c.email || t('common.none')}</td>
                  <td data-label={t('contacts.phone')} className="text-ink-soft">{c.phone || t('common.none')}</td>
                  <td data-label={t('contacts.city')} className="text-ink-soft">{c.city || t('common.none')}</td>
                  <td className="cell-actions text-right">
                    <button onClick={() => edit(c)} className="btn btn-ghost !px-2">
                      {t('common.edit')}
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(t('contacts.confirmDelete'))) remove.mutate(c.id);
                      }}
                      className="btn btn-ghost !px-2 text-expense"
                      title={t('common.delete')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedId != null && (
        <div className="reveal card mt-6 p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold">
              {t('contacts.history')} - {history.data?.displayName ?? ''}
            </h2>
            <button onClick={() => setSelectedId(null)} className="btn btn-ghost">
              {t('common.close')}
            </button>
          </div>
          {history.isLoading && <p className="text-ink-muted">{t('common.loading')}</p>}
          {history.data && (
            <>
              <div className="mb-4 flex gap-6">
                <div>
                  <p className="eyebrow">{t('contacts.totalGiven')}</p>
                  <p className="font-display text-xl font-semibold">
                    {euro(history.data.totalAmount)}
                  </p>
                </div>
                <div>
                  <p className="eyebrow">{t('contacts.entryCount')}</p>
                  <p className="font-display text-xl font-semibold">{history.data.entryCount}</p>
                </div>
              </div>
              {history.data.entries.length === 0 ? (
                <p className="text-ink-muted">{t('contacts.noHistory')}</p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-line">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{t('field.date')}</th>
                        <th>{t('field.label')}</th>
                        <th>{t('entries.campaign')}</th>
                        <th className="num">{t('receipts.amount')}</th>
                        <th className="text-right">{t('field.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.data.entries.map((row) => (
                        <tr key={row.entryId}>
                          <td data-label={t('field.date')} className="whitespace-nowrap text-ink-soft">
                            {dateFmt(row.entryDate)}
                          </td>
                          <td data-label={t('field.label')} className="font-medium">{row.label}</td>
                          <td data-label={t('entries.campaign')} className="text-ink-soft">
                            {row.campaignLabel || t('common.none')}
                          </td>
                          <td data-label={t('receipts.amount')} className="num">{euro(row.amount)}</td>
                          <td className="cell-actions text-right">
                            <button
                              onClick={() => generateReceipt.mutate(row)}
                              disabled={generateReceipt.isPending}
                              className="btn btn-ghost !px-2"
                              title={t('contacts.generateReceipt')}
                            >
                              <FileText size={16} /> {t('receipts.download')}
                            </button>
                            <button
                              onClick={() => sendReceipt.mutate(row)}
                              disabled={sendReceipt.isPending}
                              className="btn btn-ghost !px-2"
                              title={t('receipts.send')}
                            >
                              <Mail size={16} /> {t('receipts.send')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
